/*
 * The crew's Schedule tab (owner, Oct 31: Gabriel saw his schedule but no route). With routes on, a crew member gets the
 * same Route button as the office: their own stops for the day in driving order, the map with the line, and Open in Google
 * Maps. Read only; the office changes the order. The phone opens on whichever of the two it was left on, as the office's.
 */
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { addDays, type VisitCard } from "@steward/shared";
import { useMyRoute } from "../api/hooks";
import { crewWords, useSession } from "../app/session";
import { dayDate, timeRange, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Button, EmptyState, IconButton, Page, Skeleton, StatusLabel } from "../components/parts";
import { VisitMap } from "../components/VisitMap";
import { mapsLinks } from "./RouteDay";
import { readRouteFirst, saveRouteFirst } from "./Schedule";
import { SchedulePhone } from "./SchedulePhone";

const WORDS = {
  en: { route: "Route", dayBoard: "Day board", working: "Working", noAddress: "No address", openMaps: "Open in Google Maps" },
  es: { route: "Ruta", dayBoard: "Día", working: "Trabajando", noAddress: "Sin dirección", openMaps: "Abrir en Google Maps" },
};

export function MySchedule() {
  const { company } = useSession();
  const today = todayIn(company.timezone);
  const [routeDay, setRouteDay] = useState<string | null>(() => (readRouteFirst(company.trade) ? today : null));
  const route = useMyRoute(company.id, routeDay ?? today);
  const on = !!route.data?.on;
  const openRoute = (d: string) => {
    saveRouteFirst(true);
    setRouteDay(d);
  };
  if (on && routeDay)
    return (
      <MyRoute
        date={routeDay}
        onDate={setRouteDay}
        onBoard={() => {
          saveRouteFirst(false);
          setRouteDay(null);
        }}
      />
    );
  return <SchedulePhone mine {...(on ? { onRoute: openRoute } : {})} />;
}

function MyRoute({ date, onDate, onBoard }: { date: string; onDate: (d: string) => void; onBoard: () => void }) {
  const { company, me } = useSession();
  const navigate = useNavigate();
  const crew = crewWords(me.locale);
  const w = WORDS[me.locale === "es" ? "es" : "en"];
  const today = todayIn(company.timezone);
  const route = useMyRoute(company.id, date);
  const [hover, setHover] = useState<string | null>(null);
  const stops = route.data?.visits ?? [];
  const path = route.data?.path ?? null;
  const color = stops[0]?.crew[0]?.color ?? "var(--color-interactive)";
  const left = stops.filter((v) => v.status !== "done");
  const links = mapsLinks("", left);
  const numberOf = new Map(stops.map((v, i) => [v.ref, i + 1]));
  const open = (v: VisitCard) => void navigate({ to: "/my-day/$ref", params: { ref: v.ref } });

  return (
    <Page title={dayDate(date)} subtitle={w.route}>
      <div className="row tight">
        <Button type="secondary" size="small" icon="schedule" onClick={onBoard}>
          {w.dayBoard}
        </Button>
        <IconButton icon="arrowLeft" label={crew.dayBefore} onClick={() => onDate(addDays(date, -1))} />
        {date === today ? null : (
          <Button type="tertiary" size="small" onClick={() => onDate(today)}>
            {crew.today}
          </Button>
        )}
        <IconButton icon="arrowRight" label={crew.dayAfter} onClick={() => onDate(addDays(date, 1))} />
      </div>
      {!route.data ? (
        <Skeleton rows={6} />
      ) : !stops.length ? (
        <EmptyState icon="schedule" title={crew.noJobsDay}>
          {crew.noJobsDayHint}
        </EmptyState>
      ) : (
        <div className="route-grid">
          <div className="route-map">
            <VisitMap
              visits={stops}
              active={hover}
              numberOf={(v) => numberOf.get(v.ref) ?? 0}
              colorOf={() => color}
              lines={
                path?.roads
                  ? [{ color, points: path.points, roads: true }]
                  : [{ color, points: stops.filter((v) => v.lat !== null && v.lng !== null).map((v) => [v.lat!, v.lng!]) }]
              }
              home={path?.home ?? null}
            />
          </div>
          <div className="route-list">
            <section className="route-group" style={{ ["--rc" as string]: color }} aria-label={w.route}>
              <ol className="route-stops">
                {stops.map((v, i) => (
                  <li
                    key={v.ref}
                    className={`route-stop${v.status === "done" ? " done" : ""}${hover === v.ref ? " on" : ""}`}
                    onMouseEnter={() => setHover(v.ref)}
                    onMouseLeave={() => setHover(null)}
                  >
                    <span />
                    <span className="route-num num">
                      {v.status === "done" ? <Icon name="check" size={12} label={crew.done} /> : i + 1}
                    </span>
                    <button type="button" className="route-stop-main" onClick={() => open(v)}>
                      <b>{v.clientName}</b>
                      <span className="muted small">
                        {[v.address ?? w.noAddress, v.startTime ? timeRange(v.startTime, v.endTime) : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </button>
                    {v.status === "in_progress" ? <StatusLabel status="informative">{w.working}</StatusLabel> : null}
                  </li>
                ))}
              </ol>
              {links.length ? (
                <div className="row tight">
                  {links.map((l) => (
                    <a key={l.href} className="btn secondary small" href={l.href} target="_blank" rel="noreferrer">
                      {links.length === 1 ? w.openMaps : l.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </section>
          </div>
        </div>
      )}
    </Page>
  );
}
